/**
 * Shipment "view tabs" — saved views promoted to their own tab in the Shipment module.
 *
 * Storage rides on the existing per-user saved-views API rather than adding a new
 * one: each pinned tab is one saved-view record under a dedicated page key, whose
 * `name` is the tab label and whose `config` is the table configuration the tab
 * opens with. That gives cross-login persistence for free (the records are scoped
 * to the bearer token, server-side) with no backend change.
 *
 * The config only ever carries keys `buildViewConfig` already names, because a
 * backend that deserialises `config` into a typed DTO drops the ones it doesn't
 * declare — see useTableSavedViews.extraConfig.test.js. Anything tab-specific must
 * therefore live in a first-class column (`name`), never inside the blob.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { tableSavedViewsApi, TABLE_PAGE_KEYS } from "../../../TableSavedViews";
import { nextViewTabOrder, reorderViewTabs, sortViewTabs } from "./viewTabOrder";

/** Page key the tab records themselves are filed under. */
export const SHIPMENT_VIEW_TABS_PAGE_KEY = TABLE_PAGE_KEYS.SHIPMENT_VIEW_TABS;

/**
 * Redux tab id / saved-views page key for a tab's own table. Each tab gets its own
 * namespace so its column layout, sort and filters are independent of the main list
 * and of every other tab.
 */
export const viewTabStateKey = (viewTabId) => `view-tab-${viewTabId}`;

const isPlainConfig = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/** Same tolerance as the saved-views hook: `config` may come back as JSON text. */
const normalizeConfig = (config) => {
  if (typeof config === "string") {
    try {
      const parsed = JSON.parse(config);
      return isPlainConfig(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return isPlainConfig(config) ? config : {};
};

const toViewTab = (record) => {
  const config = normalizeConfig(record.config);
  return {
    id: record.id,
    name: record.name,
    config,
    // Position lives in the config blob for want of a field of its own; lifted out
    // here so nothing downstream has to know that.
    order: Number.isFinite(config.order) ? config.order : null,
    stateKey: viewTabStateKey(record.id),
  };
};

/**
 * Loads the user's shipment view tabs and exposes create/rename/retarget/delete.
 *
 * Write responses are folded in as patches over what we sent rather than adopted
 * wholesale, for the same reason the saved-views hook does it: deployments answer
 * these endpoints with empty or partial bodies, and trusting those would blank a
 * tab's name or config the moment it was edited.
 */
export function useShipmentViewTabs({ enabled = true } = {}) {
  const [viewTabs, setViewTabs] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  // Reordering has to read the whole current list and then fire writes, so it needs
  // the list outside of a setState updater.
  const viewTabsRef = useRef(viewTabs);
  viewTabsRef.current = viewTabs;

  const reload = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await tableSavedViewsApi.list(SHIPMENT_VIEW_TABS_PAGE_KEY);
      setViewTabs(sortViewTabs((Array.isArray(data) ? data : []).map(toViewTab)));
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to load tabs"
      );
      setViewTabs([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  const addViewTab = useCallback(
    async (name, config) => {
      // New tabs go to the end of the bar.
      const configWithOrder = {
        ...(config || {}),
        order: nextViewTabOrder(viewTabsRef.current),
      };
      const { data } = await tableSavedViewsApi.create({
        pageKey: SHIPMENT_VIEW_TABS_PAGE_KEY,
        name: String(name || "").trim(),
        isDefault: false,
        config: configWithOrder,
      });

      // The record's id becomes the tab's id, its saved-views page key and the
      // target of every later edit, so there is nothing usable to add without one.
      // Re-list rather than inventing an id and rendering a tab that can't be
      // renamed or removed.
      if (!isPlainConfig(data) || data.id == null) {
        await reload();
        return null;
      }

      // Keep the config we sent underneath the returned one — a backend that
      // narrows unknown keys would otherwise hand back a tab that opens on
      // default columns, and with no order at all.
      const created = toViewTab({
        ...data,
        config: { ...configWithOrder, ...normalizeConfig(data.config) },
      });
      setViewTabs((prev) => sortViewTabs([...prev, created]));
      return created;
    },
    [reload]
  );

  const renameViewTab = useCallback(async (viewTabId, name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return null;
    await tableSavedViewsApi.update(viewTabId, { name: trimmed });
    setViewTabs((prev) =>
      prev.map((t) => (String(t.id) === String(viewTabId) ? { ...t, name: trimmed } : t))
    );
    return trimmed;
  }, []);

  const updateViewTabConfig = useCallback(async (viewTabId, config) => {
    const existing = viewTabsRef.current.find(
      (t) => String(t.id) === String(viewTabId)
    );
    // A PUT replaces the whole blob, and the layout captured from the table knows
    // nothing about ordering — carry the tab's position across or it moves to the
    // end of the bar every time its layout is saved.
    const nextConfig = { ...(config || {}) };
    if (Number.isFinite(existing?.order)) nextConfig.order = existing.order;

    await tableSavedViewsApi.update(viewTabId, { config: nextConfig });
    setViewTabs((prev) =>
      prev.map((t) =>
        String(t.id) === String(viewTabId) ? { ...t, config: nextConfig } : t
      )
    );
  }, []);

  const removeViewTab = useCallback(async (viewTabId) => {
    await tableSavedViewsApi.remove(viewTabId);
    setViewTabs((prev) => prev.filter((t) => String(t.id) !== String(viewTabId)));
  }, []);

  /**
   * Drop one tab onto another's slot. Applied locally first so the tab bar responds
   * immediately, then persisted; a failed write re-lists so the UI can't keep
   * showing an order the server didn't accept.
   */
  const moveViewTab = useCallback(
    async (sourceId, targetId) => {
      const result = reorderViewTabs(viewTabsRef.current, sourceId, targetId);
      if (!result) return;

      setViewTabs(result.tabs);

      try {
        await Promise.all(
          result.changed.map((tab) =>
            tableSavedViewsApi.update(tab.id, { config: tab.config })
          )
        );
      } catch (err) {
        await reload();
        throw err;
      }
    },
    [reload]
  );

  return useMemo(
    () => ({
      viewTabs,
      loading,
      error,
      reload,
      addViewTab,
      renameViewTab,
      updateViewTabConfig,
      removeViewTab,
      moveViewTab,
    }),
    [
      viewTabs,
      loading,
      error,
      reload,
      addViewTab,
      renameViewTab,
      updateViewTabConfig,
      removeViewTab,
      moveViewTab,
    ]
  );
}

const ShipmentViewTabsContext = createContext(null);

/**
 * One shared copy of the tab list for the whole Shipment module: the tab bar reads
 * it to render tabs, and each table's Tabs control writes to it.
 */
export const ShipmentViewTabsProvider = ({ value, children }) => (
  <ShipmentViewTabsContext.Provider value={value}>
    {children}
  </ShipmentViewTabsContext.Provider>
);

/** Null outside the provider, so tables used elsewhere simply hide the control. */
export const useShipmentViewTabsContext = () => useContext(ShipmentViewTabsContext);
